import React, { forwardRef, useId } from "react";
import { cn } from "@/lib/utils";

/**
 * Frac Input primitive.
 *
 * Props:
 *   label        — visible label above the input (13/600)
 *   hint         — optional 12.5 helper text below
 *   error        — inline error message (shown in place of hint when set)
 *   onDark       — flips styling to dark-surface treatment
 *   All native <input> props are forwarded (value, onChange, type, ...).
 */
export const Input = forwardRef(function Input(
  {
    label,
    hint,
    error,
    onDark = false,
    className,
    id: idProp,
    disabled,
    "data-testid": testId,
    ...rest
  },
  ref,
) {
  const autoId = useId();
  const id = idProp ?? `input-${autoId.replace(/[^a-zA-Z0-9-]/g, "")}`;
  const errorId = error ? `${id}-error` : undefined;
  const hintId = hint && !error ? `${id}-hint` : undefined;
  const describedBy = errorId || hintId;

  return (
    <div className={cn("grid gap-1.5", className)}>
      {label && (
        <label
          htmlFor={id}
          className={cn(
            "text-[13px] font-semibold leading-tight",
            onDark ? "text-ink-primary" : "text-frac-purple",
          )}
        >
          {label}
        </label>
      )}
      <input
        ref={ref}
        id={id}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        data-testid={testId}
        className={cn(
          "block h-10 w-full rounded-[10px] border px-3 py-[9px] text-[15px] leading-tight transition-colors duration-fast ease-out",
          "focus:outline-none focus:ring-[3px]",
          "disabled:opacity-60 disabled:cursor-not-allowed",
          onDark
            ? "bg-[rgba(237,241,245,0.04)] text-ink-primary placeholder:text-frac-gray-50 border-hairline focus:border-frac-violet focus:ring-frac-violet/15"
            : "bg-white text-frac-black placeholder:text-frac-gray-50 border-frac-gray-30 focus:border-frac-violet focus:ring-frac-violet/15",
          error &&
            "!border-[#A11C1C] focus:!border-[#A11C1C] focus:!ring-[#A11C1C]/15",
        )}
        {...rest}
      />
      {(error || hint) && (
        <p
          id={errorId ?? hintId}
          data-testid={
            testId ? (error ? `${testId}-error` : `${testId}-hint`) : undefined
          }
          className={cn(
            "text-[12.5px] leading-snug",
            error
              ? "text-[#A11C1C]"
              : onDark
                ? "text-ink-tertiary"
                : "text-frac-gray-70",
          )}
        >
          {error || hint}
        </p>
      )}
    </div>
  );
});

export default Input;
